/* ============================================================================
 * Pawn Shop tutorial — the content
 * ----------------------------------------------------------------------------
 * A screen-by-screen walkthrough of the pawn module, written for a teller who
 * has never opened it before. Pure DATA: every chapter is a list of typed blocks
 * that TutorialBlocks.js knows how to render, so the copy can be edited (or
 * translated) without touching any layout code.
 *
 * Chapter shape
 *   { id, title, navTitle?, tagline, icon, path?, pathLabel?, blocks: [] }
 *
 * `navTitle` is the short label for the chapter rail, which is only ~270px wide
 * — a title like "Payment Screen — issuing a loan" would be clipped mid-word
 * there. Chapters whose full title already fits omit it.
 *
 * Block types
 *   h        { text }                          – sub-heading inside a chapter
 *   para     { text }                          – paragraph (**bold**, `code`)
 *   bullets  { items:[text] }                  – unordered list
 *   steps    { items:[{label,text}] }          – numbered "do this, then this"
 *   fields   { items:[{name,text,tag?}] }      – field-by-field reference
 *   example  { title, subtitle?, rows, total?, foot? }
 *                                              – a WORKED calculation in rupees
 *   table    { head:[], rows:[[]], foot? }     – small comparison grid
 *   callout  { tone:'info'|'tip'|'warn', title?, text }
 *   flow     { items:[{label,text}] }          – left-to-right stage diagram
 *   kbd      { items:[{keys,text}] }           – keyboard / scanner shortcuts
 *
 * The numbers below are ONE running story (Lakshmi Devi's bangle loan of
 * 10-Apr-2026) carried from the loan through every receipt to settlement, so the
 * figures in a later chapter are the figures the earlier chapter produced. They
 * are computed with the same rules the app uses — see
 * utils/pawnInterestCalculator.js, which is the engine both this text and the
 * screens describe.
 * ========================================================================== */

import React from "react";
import {
  WavingHandRounded as WelcomeIcon,
  DashboardRounded as DashboardIcon,
  DiamondRounded as MastersIcon,
  SettingsRounded as SettingsIcon,
  PeopleRounded as CustomerIcon,
  PaymentRounded as PaymentIcon,
  CalculateRounded as EngineIcon,
  ReceiptLongRounded as ReceiptIcon,
  TaskAltRounded as SettlementIcon,
  ReceiptRounded as TicketIcon,
  QrCodeScannerRounded as BarcodeIcon,
  InventoryRounded as StockIcon,
  AssessmentRounded as ReportsIcon,
  AssuredWorkloadRounded as AccountsIcon,
  EmojiEventsRounded as FinishIcon,
} from "@mui/icons-material";

/* The store settings the whole tutorial assumes, stated once so every worked
 * example can be checked against them. These are the values a typical gold
 * counter runs with — your own Settings screen may differ, and the tutorial says
 * so wherever the answer changes. */
export const DEMO_SETTINGS = [
  { label: "Master interest", value: "1.16 % per month" },
  { label: "Master insurance", value: "0.10 % per month" },
  { label: "Ticket prefix", value: "PW (prefix on)" },
  { label: "One month deduct", value: "ON" },
  { label: "Pawn monthly interest", value: "ON (month-wise, not day-wise)" },
  { label: "No interest within 2 days", value: "ON" },
  { label: "< 15 days = half month", value: "ON" },
  { label: "Pawn compound interest", value: "ON (yearly)" },
];

const CHAPTERS = [
  /* ══════════════════════════════════════════════════════════════════════════
   * 1. Welcome
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "welcome",
    navTitle: "Welcome",
    title: "Welcome to the Pawn Shop module",
    tagline: "What this tutorial covers, and how a loan travels through the app",
    icon: <WelcomeIcon />,
    blocks: [
      {
        type: "para",
        text:
          "This is a guided tour of every screen in the pawn module. Each chapter " +
          "explains what a screen is for, walks its fields one by one, and then " +
          "works a real example in rupees so you can see the arithmetic the app " +
          "is doing. Read it end to end once and you will know the whole counter.",
      },
      {
        type: "callout",
        tone: "tip",
        title: "How to use it",
        text:
          "Use **Next** at the bottom to move forward, or jump straight to any " +
          "chapter from the list. Every chapter has an **Open this screen** " +
          "button — press it to try the real screen, then come back to Tutorial " +
          "and you will land on the same chapter you left.",
      },
      { type: "h", text: "The life of a loan" },
      {
        type: "para",
        text:
          "Almost everything in this module is one of four moments in the life of " +
          "a single pledge. Learn these four and the rest is supporting cast.",
      },
      {
        type: "flow",
        items: [
          {
            label: "Payment",
            text: "The customer pledges ornaments and walks out with cash. One pawn ticket is born.",
          },
          {
            label: "Receipt",
            text: "Month after month the customer pays interest — and sometimes part of the principal.",
          },
          {
            label: "Settlement",
            text: "The last payment clears principal + dues. Ornaments go back, the ticket closes.",
          },
          {
            label: "Reports",
            text: "Every one of those movements shows up in the registers and in the ledger.",
          },
        ],
      },
      { type: "h", text: "The example we will follow" },
      {
        type: "para",
        text:
          "So the numbers join up, one customer runs through the whole tutorial: " +
          "**Lakshmi Devi** pledges a gold bangle on **10-Apr-2026** and borrows " +
          "₹1,50,000. You will watch that loan get issued, get its interest " +
          "collected three different ways, get part of its principal repaid, and " +
          "finally get settled.",
      },
      {
        type: "table",
        head: ["Setting", "Value used throughout"],
        rows: DEMO_SETTINGS.map((s) => [s.label, s.value]),
        foot:
          "Your store's own values live in Settings. Wherever a setting changes " +
          "the answer, the chapter shows both outcomes.",
      },
      {
        type: "callout",
        tone: "info",
        title: "Order of the tour",
        text:
          "Chapters 3–5 are the one-time setup (metals, purities, floors, " +
          "interest rules, schemes, customers). Chapters 6–9 are the daily work. " +
          "Chapters 10–14 are printing, stock and reporting. If your shop is " +
          "already live, you can start at chapter 6.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 2. Dashboard
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "dashboard",
    title: "Dashboard",
    tagline: "Your counter at a glance, and the fastest way into any screen",
    icon: <DashboardIcon />,
    path: "/pawn/home",
    blocks: [
      {
        type: "para",
        text:
          "The dashboard is the screen you land on after login. It answers the " +
          "three questions a shop owner asks every morning — how much money is " +
          "out on loan, what happened today, and how much came back — and gives " +
          "you one-tap shortcuts into the working screens.",
      },
      { type: "h", text: "The stat tiles" },
      {
        type: "fields",
        items: [
          {
            name: "Active Loan Book",
            text: "Principal still outstanding across every open ticket. This is the money on the street.",
          },
          {
            name: "Loans Issued Today",
            text: "Count of pledges booked today — a quick check against the physical tickets in the drawer.",
          },
          {
            name: "Disbursed This Month",
            text: "Total paid out to customers this month. Compare it against your cash and bank movements.",
          },
          {
            name: "Settled / Closed",
            text: "Tickets closed out — ornaments already returned to the customer.",
          },
          {
            name: "Total Customers",
            text: "Everyone in the customer master, whether they currently hold a loan or not.",
          },
        ],
      },
      {
        type: "example",
        title: "Reading the tiles on a normal day",
        subtitle: "A single-counter gold shop, mid-month",
        rows: [
          { label: "Active Loan Book", value: "₹42,80,000", note: "312 open tickets" },
          { label: "Loans Issued Today", value: "7", note: "₹4,15,000 disbursed" },
          { label: "Disbursed This Month", value: "₹38,60,000" },
          { label: "Settled / Closed", value: "23 this month" },
        ],
        total: { label: "Average active loan", value: "₹13,718" },
        foot:
          "₹42,80,000 ÷ 312 tickets = ₹13,718 per ticket. The dashboard computes " +
          "this for you — a sudden jump in the average usually means one unusually " +
          "large pledge that is worth a second look.",
      },
      { type: "h", text: "Quick actions" },
      {
        type: "para",
        text:
          "Below the tiles sit shortcuts to **Payment Screen**, **Receipt " +
          "Screen**, **Settlement Screen**, **Reports** and **Settings**. They go " +
          "to exactly the same places as the sidebar; they are there so a busy " +
          "counter never has to hunt.",
      },
      {
        type: "callout",
        tone: "tip",
        text:
          "The sidebar collapses to a narrow icon rail to give the screen more " +
          "room. On a desktop it opens when you move the mouse over it; on a " +
          "tablet, tap the menu button on the rail; on a phone, use the ☰ button " +
          "in the top bar.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 3. Masters — metal type, floor
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "masters",
    navTitle: "Metal Type & Floor",
    title: "Metal Type & Floor masters",
    tagline: "Set these up once — they drive valuation and where the pouch is stored",
    icon: <MastersIcon />,
    path: "/pawn/masters",
    pathLabel: "Open Masters",
    blocks: [
      {
        type: "para",
        text:
          "This screen has two chips at the top: **Metal Type** and " +
          "**Floor**. Both are one-time setup, and both show up as dropdowns on " +
          "the Payment Screen — so five minutes here saves typing on every loan " +
          "you will ever book.",
      },
      { type: "h", text: "Metal Type" },
      {
        type: "para",
        text:
          "One row per metal you lend against — GOLD, SILVER and so on. There " +
          "is no separate purity master: a purity is not a record, it is just " +
          "the **percentage you type on the ornament row**, piece by piece. " +
          "What the metal carries is a **Default Purity %** — the figure the " +
          "ornament row opens on, so the common case needs no typing at all.",
      },
      {
        type: "table",
        head: ["Metal", "Default Purity %", "Ornament row opens on", "Everyday name"],
        rows: [
          ["GOLD", "91.60", "91.6", "22 carat hallmark"],
          ["SILVER", "92.50", "92.5", "Sterling silver"],
        ],
        foot:
          "A brand-new pawn store is seeded automatically with GOLD at 91.6, " +
          "so you can book your first loan before you have configured anything. " +
          "An 18 carat piece is not another master row — you type 75 in the " +
          "Purity % box on that ornament.",
      },
      {
        type: "callout",
        tone: "warn",
        title: "Purity % is not decoration",
        text:
          "Ornament value = **net weight × purity % × rate per gram**, and the " +
          "rate you enter is always the **100 % pure** rate. Enter 91.6 for 22 " +
          "carat and the app values 21.800 g as 19.969 g of pure gold. Enter " +
          "100 by mistake and you will over-lend by about 9 % on every single " +
          "22 carat pledge.",
      },
      { type: "h", text: "Floor" },
      {
        type: "para",
        text:
          "A floor is a **physical storage place** — a strong room, a locker, a " +
          "shelf. Floors are grouped under locations, so a two-branch shop can " +
          "keep \"Main Branch · Strong Room\" separate from \"Market Branch · " +
          "Locker 2\". Every loan is tagged with the floor its pouch went into.",
      },
      {
        type: "bullets",
        items: [
          "Name floors the way your staff actually speak: `Strong Room · Shelf A-2`, not `SR2`.",
          "Once a loan is tagged, the **Floor Report** can tell you exactly which tickets and how many grams are sitting on each shelf as on any date.",
          "At audit time the Floor Report is what you count against — so a floor that is never selected on the Payment Screen is a floor you cannot verify.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text:
          "These three masters are shared with the jewellery side of the product, " +
          "which is why they look slightly different from a pure pawn screen. The " +
          "data is per store — configuring one counter does not touch another.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 4. Settings & schemes
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "settings",
    navTitle: "Settings & Schemes",
    title: "Settings & Scheme master",
    tagline: "The rules of your shop: rates, interest behaviour, ledger accounts, ticket numbers",
    icon: <SettingsIcon />,
    path: "/pawn/settings",
    blocks: [
      {
        type: "para",
        text:
          "This is the most important screen in the module and the one you will " +
          "visit least. Everything the interest engine does later is decided " +
          "here. The left column is configuration; the right column is the " +
          "**Scheme master**.",
      },
      { type: "h", text: "Global master" },
      {
        type: "fields",
        items: [
          {
            name: "Interest",
            tag: "% per month",
            text: "Your default monthly rate, used whenever a loan is booked without a scheme. Capped at 1.16 % — the legal ceiling of 13.92 % a year.",
          },
          {
            name: "Insurance",
            tag: "% per month",
            text: "A monthly charge on the principal that follows every interest rule exactly; only the rate differs. Leave it at 0 and insurance disappears from the whole module.",
          },
          {
            name: "Prefix / Use prefix in ticket",
            text: "Up to 5 characters stamped in front of ticket numbers. With prefix `PW`, tickets read PW1, PW2, PW3…; without it, plain 1, 2, 3.",
          },
          {
            name: "Manual voucher number",
            text: "Lets you type the ticket number yourself when creating a payment instead of taking the next automatic one. The app checks as you type and blocks Save if that number is already used. An existing loan's number can never be changed.",
          },
          {
            name: "OTP validation for customer creation",
            text: "A new customer is only created after an OTP sent to the mobile number you entered is verified. Good discipline against wrong phone numbers.",
          },
        ],
      },
      {
        type: "callout",
        tone: "tip",
        title: "Changing the prefix mid-year",
        text:
          "Type a new prefix and the screen tells you what that prefix will do " +
          "— whether it is brand new, or where its numbering resumes — and offers " +
          "to either continue the running number or restart at 1. Nothing is " +
          "decided until you press Save.",
      },
      { type: "h", text: "Account mapping" },
      {
        type: "para",
        text:
          "Six dropdowns tie the pawn counter to your books. Every loan and every " +
          "receipt posts a double-entry voucher using these, which is why the " +
          "Ledger and Cash Book agree with the pawn registers without anyone " +
          "keying anything twice.",
      },
      {
        type: "fields",
        items: [
          { name: "Pledge Account", text: "The asset account that carries loans outstanding — debited when you disburse, credited when principal comes back." },
          { name: "Cash Account", text: "Your counter cash. The default payment account on both the Payment and Receipt screens." },
          { name: "UPI Account", text: "The bank account pre-selected for the transfer leg of a split payment." },
          { name: "Interest Account", text: "Income account credited with interest collected." },
          { name: "Insurance Account", text: "Income account credited with insurance collected." },
          { name: "Other Charges Account", text: "Income account for document / pouch / service charges." },
        ],
      },
      { type: "h", text: "Payment, receipt & settlement rules" },
      {
        type: "fields",
        items: [
          {
            name: "One month deduct",
            tag: "Payment",
            text: "Takes the first month's interest (and insurance) out of the cash you hand over. Because month one is already collected, the customer's first receipt owes nothing for it.",
          },
          {
            name: "OTP validation for settlement",
            tag: "Settlement",
            text: "A loan can only be closed after an OTP sent to the customer's mobile is verified — protection against ornaments being released to the wrong person.",
          },
          {
            name: "Pawn monthly interest",
            tag: "Receipt",
            text: "ON = interest is charged by the month. OFF = day-wise. This single switch changes more of the arithmetic than any other setting.",
          },
          {
            name: "No interest within 2 days",
            tag: "Receipt",
            text: "A 2-day grace after a due date. Pay on the due date or up to 2 days late and the extra days cost nothing.",
          },
          {
            name: "< 15 days = half month",
            tag: "Receipt",
            text: "Pay within 15 days of the due date and you are charged half a month instead of a whole one — and you are settled through the full cycle.",
          },
          {
            name: "Pawn compound interest",
            tag: "Receipt",
            text: "After a year, whatever interest is still unpaid is added to the principal, and interest from then on runs on the bigger figure.",
          },
        ],
      },
      {
        type: "callout",
        tone: "info",
        text:
          "Every one of these rules has a small ⓘ beside it on the real screen. " +
          "Tap it for the same explanation in one sentence — handy when a customer " +
          "is standing in front of you asking why the number is what it is.",
      },
      { type: "h", text: "Scheme master" },
      {
        type: "para",
        text:
          "A scheme is a named rate you can pick from a dropdown on the Payment " +
          "Screen instead of typing a percentage. Its real power is the " +
          "**month-wise interest** option: one scheme can charge a different rate " +
          "depending on how long the loan has been running.",
      },
      {
        type: "example",
        title: "Scheme \"Gold Standard\" on a ₹1,50,000 loan",
        subtitle: "Month-wise interest on; the tier is picked by full months since the loan date",
        rows: [
          { label: "Up to 1 month — 0.90 %", value: "₹1,350 / month" },
          { label: "1 to 3 months — 1.00 %", value: "₹1,500 / month" },
          { label: "3 to 12 months — 1.16 %", value: "₹1,740 / month" },
          { label: "After 12 months — 1.50 %", value: "₹2,250 / month" },
        ],
        foot:
          "A customer who redeems inside a month pays the cheap early rate; one " +
          "who lets the loan run past a year pays the expensive late rate. Leave " +
          "the band columns empty and the scheme's base rate applies for the whole " +
          "life of the loan.",
      },
      {
        type: "callout",
        tone: "warn",
        title: "Rates are per month, never per year",
        text:
          "Every percentage in this module is a **monthly** rate. 1.16 % a month " +
          "is 13.92 % a year. Typing 13.92 into the interest field would charge " +
          "your customer 167 % annually.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 5. Customer master
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "customer",
    title: "Customer master",
    tagline: "KYC, nominees and documents — the file you build once and reuse forever",
    icon: <CustomerIcon />,
    path: "/pawn/customer-master",
    blocks: [
      {
        type: "para",
        text:
          "Every loan belongs to a customer record. Build the record properly the " +
          "first time and issuing that customer's next loan takes seconds — " +
          "search, pick, done. You can create customers here in advance, or on " +
          "the fly from the Payment Screen.",
      },
      { type: "h", text: "The customer form" },
      {
        type: "fields",
        items: [
          { name: "Party Code", text: "The internal code for the customer. Use it as a short handle on the counter." },
          { name: "Name", tag: "required", text: "As it appears on the ID you are photographing. This name is printed on the pawn ticket." },
          { name: "Mobile", text: "Carries the weight of the whole security model — OTPs for customer creation and for settlement both go here. Get it wrong and the customer cannot redeem without help." },
          { name: "Date of Birth", text: "Used for age proof and for spotting two customers with the same name." },
          { name: "Relation Type / Relative Name", text: "The \"S/W/D of\" line — son of, wife of, daughter of. Printed on the ticket and the standard way Indian pawn registers disambiguate names." },
          { name: "Annual Income", text: "Recorded for KYC thresholds. Not used in any calculation." },
          { name: "Address", text: "Printed on the ticket and shown on every report." },
          { name: "Note", text: "Free text for the counter — \"always pays late\", \"speaks Kannada only\". Never printed." },
        ],
      },
      { type: "h", text: "Documents" },
      {
        type: "para",
        text:
          "Attach the ID proofs with a **Document Number / ID** against each, and " +
          "photograph them straight from the device camera or pick a file. " +
          "Aadhaar and PAN get their own verified ticks that show up beside the " +
          "customer's name on the Payment Screen, so the teller can see at a " +
          "glance that KYC is complete.",
      },
      { type: "h", text: "Nominees" },
      {
        type: "para",
        text:
          "A nominee is who may act on the pledge if the customer cannot. Each " +
          "nominee carries a name, mobile, address, relation, a **share %** and a " +
          "**Primary Nominee** flag, and can hold its own documents. On the " +
          "Payment Screen the nominee dropdown lists this customer's nominees, " +
          "with an \"add nominee\" entry at the bottom so you never have to leave " +
          "the loan to add one.",
      },
      {
        type: "example",
        title: "A complete customer file",
        subtitle: "Lakshmi Devi — the customer we will lend to in the next chapter",
        rows: [
          { label: "Name", value: "Lakshmi Devi" },
          { label: "S/W/D of", value: "W/o Ramesh Kumar" },
          { label: "Mobile", value: "98450 xxxxx", note: "OTP target" },
          { label: "Address", value: "No. 14, 2nd Cross, Chamarajpet" },
          { label: "Documents", value: "Aadhaar ✓ · PAN ✓", note: "photographed at the counter" },
          { label: "Nominee", value: "Ramesh Kumar · Husband · 100 %", note: "primary" },
        ],
        foot:
          "Two minutes of work, and every future loan for Lakshmi starts from a " +
          "verified record with a nominee already attached.",
      },
      {
        type: "callout",
        tone: "tip",
        title: "Fingerprints",
        text:
          "Where a fingerprint scanner is attached to the counter PC, the Payment " +
          "Screen can capture two prints against the loan. The Payment Report then " +
          "shows a tick or a cross per ticket, so you can see instantly which " +
          "pledges have biometric proof behind them.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 6. Payment screen
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "payment",
    navTitle: "Payment Screen",
    title: "Payment Screen — issuing a loan",
    tagline: "Weigh, value, lend, pay out and print — the busiest screen in the shop",
    icon: <PaymentIcon />,
    path: "/pawn/payment",
    blocks: [
      {
        type: "para",
        text:
          "This is where a pledge becomes a loan. The screen is a stack of " +
          "numbered sections that you work top to bottom; each one collapses to a " +
          "one-line summary once it is done, so a long loan still fits on a " +
          "tablet. The panel on the right keeps a running summary of the money.",
      },
      {
        type: "steps",
        items: [
          { label: "Customer Details", text: "Find or create the customer." },
          { label: "Ornaments", text: "Weigh and value each item." },
          { label: "Loan & Payment Details", text: "Decide the loan, the rate, the charges and how you are paying it out." },
          { label: "Documents & Verification", text: "Photograph the ornaments, capture OTP or fingerprints." },
          { label: "Save & Print", text: "Book the loan, print the ticket and the pouch label." },
        ],
      },
      { type: "h", text: "1 · Customer Details" },
      {
        type: "para",
        text:
          "Type a name or mobile number and suggestions appear as you go, or " +
          "press the search icon for the full lookup. New customer? The **New " +
          "Customer** button opens the same form as the customer master, right " +
          "here, and drops the new record into the loan when you are done.",
      },
      {
        type: "kbd",
        items: [
          { keys: "F1", text: "Open the customer lookup" },
          { keys: "F2", text: "Open the new-customer dialog" },
          { keys: "Ctrl + Enter", text: "Save the loan from anywhere on the form" },
        ],
      },
      {
        type: "para",
        text:
          "Once a customer is attached you also get a **Previous Loans** panel — " +
          "every ticket this customer has ever had, open or closed. Worth a glance " +
          "before lending again.",
      },
      { type: "h", text: "2 · Ornaments" },
      {
        type: "para",
        text:
          "Add one row per item. The value is computed for you, but you can " +
          "always overwrite it — clear the field to hand control back to the " +
          "calculator.",
      },
      {
        type: "fields",
        items: [
          { name: "Metal Type", text: "From the Metal Type master you set up in chapter 3. Picking a metal fills the Purity % with that metal's Default Purity." },
          { name: "Rate ₹/g (100%)", text: "The pledge rate for **pure** metal — the 24 carat rate, not the 22 carat one, because purity is applied separately. Type it once and it is saved as this metal's rate **from the Loan Date onwards**: every later loan opens with it until you type a newer one. Back-dating a loan brings that day's rate back with it, which is what makes keying in an old ledger possible." },
          { name: "Purity %", text: "Opens on the metal's Default Purity; type over it when you test a piece and find it below standard." },
          { name: "Item Type", tag: "required", text: "What the thing is: BANGLE, CHAIN, RING — picked from the metal's list in Masters → Item Type. Type the name or its short code to find it; a new one can be created right from the list. Printed on the ticket, and what the customer will ask for by name at redemption." },
          { name: "Pcs", text: "How many identical pieces this row covers — 4 bangles of the same weight can be one row." },
          { name: "Gross (g)", tag: "required", text: "Weight straight off the scale, stones, lac and all." },
          { name: "Net (g)", tag: "required", text: "Metal only, after deducting stones and filler. This — not gross — is what you lend against. Press Enter on Gross and Net is pre-filled with the same figure for plain items." },
          { name: "Value (₹)", tag: "required", text: "net × purity % × rate, rounded. Type over it if you are valuing by eye." },
        ],
      },
      {
        type: "example",
        title: "Valuing Lakshmi's bangle",
        subtitle: "1 pc · GOLD · 91.6 % · 10-Apr-2026",
        rows: [
          { label: "Gross weight", value: "22.400 g", note: "off the scale" },
          { label: "Less stones / lac", value: "0.600 g" },
          { label: "Net weight", value: "21.800 g" },
          { label: "Purity", value: "91.6 %", note: "22 carat hallmark" },
          { label: "Pure gold content", value: "19.969 g", note: "21.800 × 0.916" },
          { label: "Pledge rate", value: "₹10,000 / g" },
        ],
        total: { label: "Ornament value", value: "₹1,99,688" },
        foot:
          "21.800 × 91.6 % × ₹10,000 = ₹1,99,688. Add more rows and the Item " +
          "Value on the loan section is the sum of them all.",
      },
      { type: "h", text: "3 · Loan & Payment Details" },
      {
        type: "para",
        text:
          "Three bordered groups sit side by side — Valuation, Interest, and " +
          "Floor & Charges — and below them the payout.",
      },
      {
        type: "fields",
        items: [
          { name: "Item Value", text: "Read-only: the total of your ornament rows. The ceiling on what you may lend." },
          { name: "Loan Amount", tag: "required", text: "What you are actually lending. The quick chips fill 60 / 70 / 75 / 80 % of the item value for you, and the amount is spelled out in words underneath as a typo guard. Type more than the item value and the app pulls it back down." },
          { name: "Scheme / Interest %", text: "Pick a scheme and its rate lands in Interest %, or type a rate directly. Interest Amount = one month at that rate." },
          { name: "Select Floor", text: "Which shelf the pouch is going onto. Drives the Floor Report." },
          { name: "Tare Weight (g)", text: "Weight of the pouch, tag and seal. Kept separate so the Floor Report can reconcile total weight on a shelf against jewel weight." },
          { name: "Other Charges", text: "Document, pouch or service charges. Always deducted from the payout, whatever else is switched on." },
          { name: "Insurance Amount", text: "Shown only when \"one month deduct\" is on and you have ticked the box — insurance is collected together with that first month's interest, or not at all." },
          { name: "Deduct 1 month interest", text: "The checkbox that turns the first month's interest and insurance into a deduction from the cash you hand over." },
          { name: "Amount", text: "The payout. Read-only whenever one-month-deduct is configured, because it is derived: loan − interest − insurance − other charges." },
          { name: "Nominee / Remarks", text: "Nominee for this ticket, and free text for anything unusual." },
        ],
      },
      {
        type: "example",
        title: "From ₹1,99,688 of gold to cash across the counter",
        subtitle: "Loan booked 10-Apr-2026 · scheme 1.16 % · insurance 0.10 % · one month deduct ON",
        rows: [
          { label: "Ornament value", value: "₹1,99,688" },
          { label: "Loan amount", value: "₹1,50,000", note: "75 % chip suggested ₹1,49,766; rounded up by hand" },
          { label: "Interest — 1 month @ 1.16 %", value: "− ₹1,740" },
          { label: "Insurance — 1 month @ 0.10 %", value: "− ₹150" },
          { label: "Other charges", value: "− ₹60", note: "pouch and ticket" },
        ],
        total: { label: "Paid to the customer", value: "₹1,48,050" },
        foot:
          "The loan on the books is ₹1,50,000 — that is the principal interest " +
          "runs on. The customer receives ₹1,48,050 because month one is already " +
          "settled, which is why the first receipt will ask nothing for it.",
      },
      {
        type: "callout",
        tone: "info",
        title: "Turn the deduction off and the same loan looks like this",
        text:
          "Untick **Deduct 1 month interest** and the payout becomes ₹1,50,000 − " +
          "₹60 = ₹1,49,940. No insurance is charged at all, and the very first " +
          "receipt will ask for month one's interest. It is a per-loan decision, " +
          "stored on the ticket — a loan that opted out is never later treated as " +
          "prepaid.",
      },
      { type: "h", text: "Splitting the payout" },
      {
        type: "para",
        text:
          "Tick **Split Payment (Cash + Bank)** when the money leaves by more " +
          "than one route. You get a cash card and an **Account Transfer 1** card, " +
          "and an **Add Another** button for a second transfer. Each transfer " +
          "carries its own account and a Transaction Reference Number — the UTR, " +
          "the cheque number, whatever your bank gives you.",
      },
      {
        type: "example",
        title: "Paying out ₹1,48,050 two ways",
        rows: [
          { label: "Cash from the drawer", value: "₹50,000" },
          { label: "Transfer · HDFC Current 4471", value: "₹98,050", note: "UTR HDFC26041000731" },
        ],
        total: { label: "Total", value: "₹1,48,050" },
        foot:
          "The legs must add up to the payout exactly. If they do not, the total " +
          "line turns red and tells you what it was expecting — a cheap guard " +
          "against a mistyped digit going into the ledger.",
      },
      { type: "h", text: "4 · Documents & Verification" },
      {
        type: "para",
        text:
          "Photograph the ornaments as they were handed over — this is the " +
          "evidence that matters if there is ever a dispute about what was " +
          "pledged. Where enabled, this is also where the customer OTP is " +
          "verified and where up to two fingerprints are captured.",
      },
      { type: "h", text: "5 · Save & Print" },
      {
        type: "bullets",
        items: [
          "**Save** books the loan, posts the accounting vouchers and assigns the ticket number — `PW1042` in our example, with the ledger voucher `PAY221` behind it.",
          "**Print** renders the pawn ticket from whichever template you designed in the Ticket Studio (chapter 10).",
          "**Print Barcode** prints the pouch label on the tag printer (chapter 11).",
          "The arrows in the header step through existing tickets — one at a time, or ten at a time — so you can pull up any past loan without leaving the screen.",
        ],
      },
      {
        type: "callout",
        tone: "warn",
        title: "If Save refuses",
        text:
          "A missing mandatory field is caught before the request leaves the " +
          "browser: the offending field is outlined in red and a message tells you " +
          "which one it is. Nothing is half-saved.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 7. Interest engine
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "engine",
    navTitle: "How interest works",
    title: "How interest is calculated",
    tagline: "The five rules behind every figure the Receipt screen quotes",
    icon: <EngineIcon />,
    blocks: [
      {
        type: "para",
        text:
          "This chapter has no screen of its own — it explains the arithmetic the " +
          "next two chapters lean on. Learn it and you will be able to answer " +
          "\"why this much?\" at the counter without opening a calculator.",
      },
      { type: "h", text: "Rule 1 — everything is measured from a reference date" },
      {
        type: "para",
        text:
          "Interest is never counted from today backwards. It is counted forward " +
          "from a **reference date**, which is:",
      },
      {
        type: "bullets",
        items: [
          "the **loan date**, for a loan with no receipts and no one-month deduction;",
          "the **loan date + 1 month**, when the first month was deducted at payout;",
          "the **paid-up-to date** of the last receipt, once any receipt exists.",
        ],
      },
      {
        type: "para",
        text:
          "For Lakshmi's loan — booked 10-Apr-2026 with month one deducted — the " +
          "reference date is **10-May-2026**. Her first receipt owes nothing " +
          "before that day.",
      },
      { type: "h", text: "Rule 2 — the due date, then the precedence of three tests" },
      {
        type: "para",
        text:
          "Count the whole months from the reference date to the payment date; " +
          "that lands you on a due date. The **extra days** past that due date are " +
          "then put through three tests, in this order — the first that matches " +
          "wins.",
      },
      {
        type: "table",
        head: ["Test", "Condition", "Charged"],
        rows: [
          ["1 · Grace", "extra days ≤ 2 and \"no interest within 2 days\" is on", "the whole months only"],
          ["2 · Half month", "extra days ≤ 15 and \"< 15 days = half month\" is on", "whole months + ½"],
          ["3 · Full month", "anything else", "whole months + 1"],
        ],
        foot:
          "\"< 15 days = full month\" is the override switch: turn it on and test 2 " +
          "never fires, so any part of a month costs a full month.",
      },
      { type: "h", text: "Rule 3 — you are paid up to the day you paid for" },
      {
        type: "para",
        text:
          "Whatever is charged, the customer is credited with the matching time. " +
          "Days charged always equal days from the reference to the new paid-up-to " +
          "date — so nobody pays for the same fortnight twice. Watch the same loan " +
          "settle three different ways depending on the day she walks in:",
      },
      {
        type: "table",
        head: ["She pays on", "Days late", "Rule", "Months", "Interest", "Insurance", "Paid up to"],
        rows: [
          ["12-Jun-2026", "2", "Grace", "1", "₹1,740", "₹150", "10-Jun-2026"],
          ["13-Jun-2026", "3", "Half month", "1.5", "₹2,610", "₹225", "25-Jun-2026"],
          ["26-Jun-2026", "16", "Full month", "2", "₹3,480", "₹300", "10-Jul-2026"],
        ],
        foot:
          "Principal ₹1,50,000 · interest 1.16 % · insurance 0.10 % · reference " +
          "10-May-2026, so the due date is 10-Jun-2026. Three days late costs ₹945 " +
          "more than two — but it also buys her a fortnight, to 25-Jun.",
      },
      {
        type: "callout",
        tone: "tip",
        title: "Where 25-Jun comes from",
        text:
          "Half a month means half of **that calendar month's** days. June has 30 " +
          "days, so half is 15, and 10-Jun + 15 days = 25-Jun. In February the same " +
          "rule gives 14 days.",
      },
      { type: "h", text: "Rule 4 — monthly mode versus day-wise mode" },
      {
        type: "para",
        text:
          "Switch **Pawn monthly interest** off and interest is charged by the " +
          "day. Even then a **complete calendar month is charged one flat month's " +
          "interest** — never day-summed — so February does not come out cheaper " +
          "than March. Only the leftover days beyond the last complete month are " +
          "priced per day, at that month's daily rate rounded to the rupee.",
      },
      {
        type: "example",
        title: "Day-wise interest across a month boundary",
        subtitle: "Principal ₹1,00,000 @ 1.16 % · paid up to 1-Feb-2026 · receipt on 4-Mar-2026 · grace and half-month off",
        rows: [
          { label: "February — one complete month, flat", value: "₹1,160", note: "not 28 × ₹41" },
          { label: "March daily rate", value: "₹37 / day", note: "₹1,160 ÷ 31, rounded" },
          { label: "3 leftover days in March", value: "₹111" },
        ],
        total: { label: "Interest due", value: "₹1,271" },
        foot:
          "Rounding the daily rate before multiplying is deliberate: it keeps the " +
          "figure you quote reproducible to the rupee, however many days are " +
          "involved.",
      },
      { type: "h", text: "Rule 5 — a payment fills interest, then insurance, then principal" },
      {
        type: "para",
        text:
          "Money is always allocated in that order. Anything short stays as a " +
          "**carryover** and is added to next month's demand; anything left over " +
          "after the dues comes off the principal.",
      },
      {
        type: "example",
        title: "Three different ₹ amounts against the same ₹1,890 demand",
        subtitle: "Interest due ₹1,740 · insurance due ₹150 · principal ₹1,50,000",
        rows: [
          { label: "She pays ₹1,890", value: "dues cleared", note: "principal unchanged at ₹1,50,000" },
          { label: "She pays ₹1,000", value: "₹1,000 → interest", note: "carryover ₹740 interest + ₹150 insurance" },
          { label: "She pays ₹51,890", value: "₹50,000 → principal", note: "principal drops to ₹1,00,000" },
        ],
        foot:
          "After the third case her monthly interest falls with the principal: " +
          "₹1,00,000 × 1.16 % = ₹1,160 a month instead of ₹1,740.",
      },
      { type: "h", text: "And compounding, once a year" },
      {
        type: "para",
        text:
          "With **Pawn compound interest** on, unpaid dues do not compound month " +
          "to month — they simply carry. They are capitalised only when the loan " +
          "crosses a **one-year boundary**.",
      },
      {
        type: "example",
        title: "A loan left to run for a year",
        subtitle: "₹1,50,000 booked 10-Apr-2026, nothing paid until 10-Apr-2027",
        rows: [
          { label: "Interest, 11 months @ ₹1,740", value: "₹19,140", note: "month one was prepaid" },
          { label: "Insurance, 11 months @ ₹150", value: "₹1,650" },
          { label: "She pays", value: "₹10,000", note: "all of it goes to interest" },
          { label: "Left unpaid at the year boundary", value: "₹10,790", note: "₹9,140 interest + ₹1,650 insurance" },
        ],
        total: { label: "New principal", value: "₹1,60,790" },
        foot:
          "From 10-Apr-2027 interest runs on ₹1,60,790 — ₹1,865 a month instead " +
          "of ₹1,740. With compounding off, that ₹10,790 would keep carrying as a " +
          "due and the principal would stay at ₹1,50,000.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 8. Receipt screen
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "receipt",
    navTitle: "Receipt Screen",
    title: "Receipt Screen — collecting interest",
    tagline: "Quote the dues, take the money, explain the figure, correct it later if needed",
    icon: <ReceiptIcon />,
    path: "/pawn/receipt",
    blocks: [
      {
        type: "para",
        text:
          "Month after month customers come in to service their loans. This " +
          "screen loads one ticket, works out exactly what is owed as on the date " +
          "you choose, accepts the payment across as many tender types as you " +
          "need, and lists every earlier receipt on that loan.",
      },
      { type: "h", text: "Finding the loan" },
      {
        type: "para",
        text:
          "Type or scan the ticket number. The header arrows step through tickets " +
          "one at a time or ten at a time, exactly as on the Payment Screen. Once " +
          "a loan is open you get read-only cards for **Customer Details**, " +
          "**Loan Details** and **Ornaments Details**, so you can confirm what is " +
          "in the pouch before taking money.",
      },
      { type: "h", text: "The receipt form" },
      {
        type: "fields",
        items: [
          { name: "Receipt Date", text: "The date the money is received. Change it and every figure on the screen re-prices instantly. Freely editable — back-dating a receipt to the day the customer actually paid is normal counter practice." },
          { name: "Interest Paid Till", text: "Read-only: the new paid-up-to date this receipt will buy. This is rule 3 from the last chapter, shown to you before you commit." },
          { name: "Interest Amount", text: "Interest due as on the receipt date, including any carryover from an earlier short payment." },
          { name: "Insurance Amount", text: "Insurance due, computed by exactly the same rules." },
          { name: "Interest Discount", text: "A concession you are choosing to give. It reduces the interest due only — never insurance, never principal." },
          { name: "Other Charges", text: "Notice charges, storage, whatever this visit costs. Taken out of the money received before anything is allocated to the loan." },
          { name: "Total Amount Received", text: "What the customer is actually handing over. Type any figure — the screen shows you how it will be split." },
          { name: "Settling the loan", text: "The checkbox that turns this receipt into a close-out. Chapter 9 covers what changes." },
          { name: "Remarks", text: "Free text, stored on the receipt." },
        ],
      },
      { type: "h", text: "How the money is received" },
      {
        type: "para",
        text:
          "**Pay Mode** switches the tender: cash, bank transfer, cheque or card. " +
          "Each mode asks for what it needs — **Received Into Account**, **Cheque " +
          "Number**, **UTR Number**, or card number, expiry and holder's name. As " +
          "on the Payment Screen you can split a receipt across cash plus one or " +
          "two bank accounts, each with its own reference number.",
      },
      {
        type: "example",
        title: "Lakshmi's first receipt",
        subtitle: "Ticket PW1042 · she comes in on 12-Jun-2026 · reference date 10-May-2026",
        rows: [
          { label: "Whole months to 10-Jun-2026", value: "1" },
          { label: "Days past the due date", value: "2", note: "inside the 2-day grace" },
          { label: "Interest @ 1.16 % on ₹1,50,000", value: "₹1,740" },
          { label: "Insurance @ 0.10 %", value: "₹150" },
          { label: "Interest paid till", value: "10-Jun-2026" },
        ],
        total: { label: "She pays, in cash", value: "₹1,890" },
        foot:
          "Principal stays at ₹1,50,000. The receipt is stamped with its own " +
          "ledger voucher — `REC15` — and posts against the interest and insurance " +
          "income accounts you mapped in Settings.",
      },
      { type: "h", text: "When the customer is short" },
      {
        type: "para",
        text:
          "Take whatever they can give. The screen warns you that the amount is " +
          "below the dues and, once saved, carries the shortfall forward.",
      },
      {
        type: "example",
        title: "The same visit, but she only has ₹1,000",
        rows: [
          { label: "Interest demanded", value: "₹1,740" },
          { label: "Paid towards interest", value: "₹1,000" },
          { label: "Interest carried forward", value: "₹740" },
          { label: "Insurance carried forward", value: "₹150", note: "nothing left to allocate to it" },
        ],
        total: { label: "Next month's demand", value: "₹2,780" },
        foot:
          "₹1,740 of fresh interest + ₹150 of fresh insurance + ₹890 of carryover " +
          "= ₹2,780. The carryover is not compounded — it just waits.",
      },
      { type: "h", text: "When the customer wants to reduce the loan" },
      {
        type: "example",
        title: "A part-payment against the principal",
        subtitle: "Same visit, she hands over ₹51,890",
        rows: [
          { label: "To interest", value: "₹1,740" },
          { label: "To insurance", value: "₹150" },
          { label: "To principal", value: "₹50,000" },
        ],
        total: { label: "New principal", value: "₹1,00,000" },
        foot:
          "Her monthly interest falls to ₹1,160 and her insurance to ₹100 from " +
          "here on. This is the figure the settlement chapter starts from.",
      },
      { type: "h", text: "Explaining and correcting" },
      {
        type: "bullets",
        items: [
          "**Price Breakup** opens a customer-facing dialog that spells out the whole figure — reference date, months charged, which rule applied, what the money was allocated to — and can be printed. It answers \"why this much?\" without an argument.",
          "Every earlier receipt on the loan is listed below the form. **Click any one of them to edit it** — not just the most recent. The chain from that point is replayed, so a corrected receipt fixes every figure that followed it.",
          "While editing, the screen re-prices live and excludes the receipt being edited from the history, so it cannot double-count itself.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "Verification",
        text:
          "The **Verification** card shows what proof is on file for this loan — " +
          "OTP, fingerprints, documents. On a settlement it is the card you check " +
          "before releasing ornaments.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 9. Settlement screen
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "settlement",
    navTitle: "Settlement Screen",
    title: "Settlement Screen — closing a loan",
    tagline: "The last receipt: clear the dues, clear the principal, hand back the gold",
    icon: <SettlementIcon />,
    path: "/pawn/settlement",
    blocks: [
      {
        type: "para",
        text:
          "Settlement is the same sheet as the Receipt Screen with settlement " +
          "mode switched on — the accent turns **green** so nobody can mistake a " +
          "close-out for a routine collection. Everything you already know about " +
          "receipts still applies; this chapter is only what is different.",
      },
      { type: "h", text: "What changes" },
      {
        type: "bullets",
        items: [
          "The amount to collect is **principal + interest + insurance**, not just the dues.",
          "The closing receipt gets its own voucher series — settlements run `H1`, `H2`, `H3`… independently of the `REC` receipts, so your close-outs are easy to find in the ledger.",
          "Where **OTP validation for settlement** is on in Settings, the loan is only closed after an OTP sent to the customer's mobile is verified.",
          "Once settled, the loan becomes read-only. Nothing about it can be edited afterwards.",
        ],
      },
      {
        type: "example",
        title: "Closing Lakshmi's loan",
        subtitle: "Ticket PW1042 · settled 10-Aug-2026 · principal ₹1,00,000 after her part-payment · paid up to 10-Jul-2026",
        rows: [
          { label: "Outstanding principal", value: "₹1,00,000" },
          { label: "Interest — 1 month @ 1.16 %", value: "₹1,160" },
          { label: "Insurance — 1 month @ 0.10 %", value: "₹100" },
        ],
        total: { label: "To close the loan", value: "₹1,01,260" },
        foot:
          "Paid in cash, verified by OTP. Ledger voucher `H7`, against loan " +
          "ticket `PW1042` — that pairing is exactly what the Settlement Report " +
          "shows as Ledger No. and Rect No.",
      },
      {
        type: "callout",
        tone: "warn",
        title: "Check before you release",
        text:
          "The ornaments card lists every item as it was pledged, with the photos " +
          "taken that day. Read it against what is in the pouch **before** you " +
          "hand anything over — after the settlement saves, the ticket is locked.",
      },
      {
        type: "callout",
        tone: "tip",
        text:
          "A customer who wants to close a loan but is a few hundred short can be " +
          "given an **Interest Discount** on this screen. It is recorded as a " +
          "discount, so the concession is visible in the reports rather than " +
          "hidden in a rounded-off figure.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 10. Ticket Studio
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "ticket-studio",
    title: "Ticket Studio",
    tagline: "Design the paper your customer walks out with",
    icon: <TicketIcon />,
    path: "/pawn/ticket-generator",
    pathLabel: "Open Ticket Studio",
    blocks: [
      {
        type: "para",
        text:
          "Every shop's pawn ticket looks different — different heading, " +
          "different invocation, different terms, different language. The Ticket " +
          "Studio lets you build yours by switching sections on and off and " +
          "dragging them into order, with a live preview beside you.",
      },
      { type: "h", text: "One design per ticket type" },
      {
        type: "para",
        text:
          "Three chips at the top choose which ticket you are designing: " +
          "**Payment**, **Receipt** or **Settlement**. They are stored separately, " +
          "so a pledge ticket can carry the full items table while a receipt " +
          "carries the amounts summary instead.",
      },
      {
        type: "table",
        head: ["Section", "What it prints"],
        rows: [
          ["Heading / Invocation", "Your shop's heading or a religious invocation. Some shops put it at the top, some use it as a closing line — drag it wherever you want."],
          ["Store Details", "Name, address, licence and contact of the counter."],
          ["Ticket Number & Date", "The pawn ticket number and the loan date."],
          ["Customer Details", "Name, S/W/D of, address, mobile, photo."],
          ["Loan Details", "Principal, rate, due date, floor."],
          ["Payment Details", "How the money was paid out."],
          ["Items Table", "Every ornament with weights, purity and value."],
          ["Receipt Amounts", "On receipt and settlement tickets, the amounts summary replaces the items table."],
          ["Signatures", "Customer and authorised-signatory blocks."],
          ["Terms & Conditions", "Rich text — your own wording, in your own language."],
          ["Footer Note", "The closing line."],
        ],
      },
      { type: "h", text: "Front and back" },
      {
        type: "para",
        text:
          "Each section can be marked **front** or **back**. Anything on the back " +
          "prints on the reverse of the same sheet, which is what you want for " +
          "long terms and conditions — they would otherwise squeeze everything " +
          "else off the front. The studio orders the pages correctly for duplex " +
          "printing.",
      },
      {
        type: "bullets",
        items: [
          "Start from a quick layout, then adjust — it is faster than building from nothing.",
          "Save as a named template and mark one as **default** per ticket type; that is what the Print button uses.",
          "Keep several templates around — a full A5 ticket and a compact one, say — and switch the default when your stationery changes.",
        ],
      },
      {
        type: "callout",
        tone: "tip",
        text:
          "The preview is filled with placeholder customer and ornament data so " +
          "you can judge spacing honestly. Design against the longest name and the " +
          "widest amount you realistically expect, not against a short one.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 11. Barcode Mapper
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "barcode",
    navTitle: "Barcode & labels",
    title: "Barcode Mapper & pouch labels",
    tagline: "The little tag that ties a physical pouch back to a ticket number",
    icon: <BarcodeIcon />,
    path: "/pawn/barcode-mapper",
    pathLabel: "Open Barcode Mapper",
    blocks: [
      {
        type: "para",
        text:
          "Every pledge goes into a pouch with a printed tag. The tag carries a " +
          "barcode of the ticket number, so at redemption or at stock check you " +
          "scan instead of reading handwriting. This screen is where you design " +
          "that tag and teach the app your label printer.",
      },
      { type: "h", text: "Printer & template" },
      {
        type: "fields",
        items: [
          { name: "Template Name", tag: "required", text: "What you will call this label — \"92×15 jewel tag\", say." },
          { name: "Printer Model / Variant", text: "Which command language the printer speaks. Get this wrong and the label prints as gibberish or not at all." },
          { name: "Share Name", text: "How the desktop print bridge reaches the printer." },
          { name: "Copies", text: "How many tags per pledge — usually two, one for each end of the pouch." },
          { name: "PRN Template", text: "The raw label layout. Placeholders in it are filled with real values at print time." },
          { name: "Width mm / Height mm", text: "Physical label size. Preview and print both follow it." },
          { name: "Tag shape / Body mm / Strap mm", text: "For jewel tags: the body is the readable part, the strap is the narrow tail that wraps around a ring shank or a chain." },
        ],
      },
      { type: "h", text: "Mapping the fields" },
      {
        type: "para",
        text:
          "Each placeholder in your PRN layout is mapped to a real value — the " +
          "ticket number, the customer name, the net weight. You can truncate long " +
          "values to a character limit so a long name cannot run off the edge of " +
          "the label.",
      },
      {
        type: "callout",
        tone: "warn",
        title: "The BARCODE keyword",
        text:
          "One mapping matters more than the others: the keyword the barcode " +
          "itself encodes. If it is left unmapped the tag prints a barcode that " +
          "nothing can scan — it will look perfectly fine and be useless at the " +
          "counter.",
      },
      { type: "h", text: "Preview and test" },
      {
        type: "para",
        text:
          "The **Label Preview** renders your layout to scale, so you can check " +
          "the fit before wasting a roll. Enter a real **Voucher No** to preview " +
          "with real data.",
      },
      { type: "h", text: "How the label actually reaches a printer" },
      {
        type: "para",
        text:
          "**Print Barcode** on the Payment Screen tries the routes available to " +
          "it in order: a Bluetooth printer already connected to this device, then " +
          "the desktop print bridge for a USB or network label printer, then a " +
          "previously paired Bluetooth printer, and finally a plain browser " +
          "printout as a last resort. You do not choose — it uses the best route " +
          "that is actually there.",
      },
      {
        type: "callout",
        tone: "info",
        text:
          "Where a branch printer is shared with a sibling shop, the bridge can " +
          "cover a **closed** branch's labels from another counter. It will never " +
          "quietly print your labels in another town while both shops are open.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 12. Stock check
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "stock",
    title: "Stock Check",
    tagline: "Scan the strong room and prove what is actually there",
    icon: <StockIcon />,
    path: "/pawn/reports?type=stock",
    pathLabel: "Open Stock Check",
    blocks: [
      {
        type: "para",
        text:
          "Periodically you should physically verify that every pouch the system " +
          "thinks you are holding is really on the shelf. That is a stock check: " +
          "walk the strong room, scan each pouch, and let the app tell you what " +
          "you did not scan.",
      },
      {
        type: "steps",
        items: [
          { label: "Scan", text: "Scan a pouch barcode, or type a voucher number and press Enter. The loan appears in the grid with its customer and ornaments." },
          { label: "Work through the shelf", text: "Keep scanning. Anything already checked in this round is flagged, so a double scan is obvious." },
          { label: "Save", text: "Stamps every scanned loan with the date, time and who checked it." },
          { label: "Report", text: "Switch to the register to see checked against unchecked — the unchecked list is your exception list." },
        ],
      },
      {
        type: "example",
        title: "A quarterly count",
        subtitle: "312 active tickets on the books",
        rows: [
          { label: "Scanned and saved", value: "308 tickets" },
          { label: "Not found on the shelf", value: "4 tickets", note: "the exception list" },
          { label: "Already checked in an earlier round", value: "12", note: "flagged as you scan" },
        ],
        foot:
          "Four tickets to chase is a manageable morning. Four tickets discovered " +
          "at year-end, without a record of when each pouch was last seen, is not.",
      },
      {
        type: "callout",
        tone: "warn",
        title: "Reset clears everything",
        text:
          "**Reset Stock Check** removes the stamp from **every** loan in the " +
          "store, so the next round starts clean. It is the right thing to do at " +
          "the start of a new count and the wrong thing to do in the middle of " +
          "one. The screen shows when the last check and the last reset happened, " +
          "and who did each.",
      },
      {
        type: "callout",
        tone: "info",
        text:
          "Stock check deliberately covers the whole store's holdings — it is a " +
          "physical count of what is in the room, so it is not filtered the way " +
          "the financial registers are.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 13. Reports
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "reports",
    title: "Reports",
    tagline: "Six registers behind one row of chips",
    icon: <ReportsIcon />,
    path: "/pawn/reports",
    blocks: [
      {
        type: "para",
        text:
          "All the registers live on one screen; the chips across the top switch " +
          "between them and the choice is kept in the address, so a link to a " +
          "particular report is shareable. Every report shares the same habits, " +
          "which is the useful thing to learn once.",
      },
      { type: "h", text: "Habits every report shares" },
      {
        type: "bullets",
        items: [
          "**The page never scrolls.** The table scrolls inside its own frame, so the headings and the totals row stay where you can see them.",
          "**One date window.** A From and a To field, plus a **Single Day** checkbox that collapses the To field away when you only want today.",
          "**Dates fetch by themselves.** Change a date and the report reloads immediately. The **View** button is only needed after you type into a text search.",
          "**Sort where you stand.** A sort control sits beside View on every report, applied after filtering.",
        ],
      },
      { type: "h", text: "The five reports" },
      {
        type: "fields",
        items: [
          { name: "Payment", text: "Every loan disbursed in the window — customer, ornaments, principal, rate, payout. Your pledge register, and where the fingerprint tick per ticket shows up." },
          { name: "Receipt", text: "The pledge-and-redemption register: loans as rows, with the receipts against each nested inside. The one place you can see a loan's whole payment history at a glance." },
          { name: "Settlement", text: "Closed loans only, with the settlement receipt joined on — Ledger No. is the `H` voucher, Rect No. is the loan ticket." },
          { name: "Stock", text: "The stock check screen and its register, filtered to checked or unchecked (chapter 12)." },
          { name: "Floor", text: "A floorwise snapshot of the strong room as on a date: how many active tickets and how many grams are sitting on each shelf." },
        ],
      },
      {
        type: "example",
        title: "Floor report at a quarterly audit",
        subtitle: "Active loans as on 30-Jun-2026",
        rows: [
          { label: "Strong Room · Shelf A-2", value: "46 tickets", note: "jewel 812.400 g + tare 38.500 g = 850.900 g" },
          { label: "Strong Room · Shelf A-3", value: "38 tickets", note: "jewel 664.150 g + tare 31.000 g = 695.150 g" },
          { label: "Locker 2", value: "9 tickets", note: "jewel 402.600 g + tare 7.500 g = 410.100 g" },
        ],
        total: { label: "Total on the books", value: "93 tickets · 1,956.150 g" },
        foot:
          "Tare is tracked separately precisely so this reconciles: put the shelf " +
          "on a scale and the gross weight should match jewel + tare, not jewel " +
          "alone.",
      },
      {
        type: "example",
        title: "Cash Book for the cash account",
        subtitle: "12-Jun-2026, the day Lakshmi paid her interest",
        rows: [
          { label: "Opening balance", value: "₹1,86,400" },
          { label: "REC15 · interest received, PW1042", value: "+ ₹1,890" },
          { label: "PAY222 · new loan disbursed", value: "− ₹40,000" },
        ],
        total: { label: "Closing balance", value: "₹1,48,290" },
        foot:
          "Each line shows the other side of the voucher, which is what makes a " +
          "day book readable: you are looking at cash, so you see what the cash " +
          "went to and came from. This one lives on Ledger Reports rather than " +
          "here — Cash Book, in the chip strip (chapter 14).",
      },
      {
        type: "callout",
        tone: "tip",
        text:
          "Reports are the fastest way to answer a customer at the counter. " +
          "\"When did I last pay?\" is the Receipt report. \"Which shelf is my " +
          "bangle on?\" is the Floor report. \"Did that transfer land?\" is the " +
          "Cash Book on Ledger Reports.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 14. Accounts & ledger
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "accounts",
    title: "Accounts & Ledger",
    tagline: "Where the pawn counter meets your books",
    icon: <AccountsIcon />,
    path: "/pawn/accounts",
    pathLabel: "Open Account Master",
    blocks: [
      {
        type: "para",
        text:
          "The sidebar's last pair of screens are the shared accounting screens, " +
          "mounted inside the pawn module and wearing pawn colours. Nothing needs " +
          "re-keying: the vouchers are already there, posted as each loan and " +
          "receipt was saved.",
      },
      {
        type: "fields",
        items: [
          { name: "Account Master", text: "Your chart of accounts. The six dropdowns in pawn Settings point at accounts from this list." },
          { name: "Ledger", text: "Any account's full transaction history with a running balance." },
        ],
      },
      { type: "h", text: "What a loan looks like in the books" },
      {
        type: "example",
        title: "The voucher behind Lakshmi's ₹1,50,000 loan",
        subtitle: "Voucher PAY221 · 10-Apr-2026 · one month deducted, ₹1,48,050 paid out",
        rows: [
          { label: "Pledge Account", value: "Dr ₹1,50,000", note: "the loan becomes an asset" },
          { label: "Cash Account", value: "Cr ₹50,000", note: "cash leg of the payout" },
          { label: "HDFC Current 4471", value: "Cr ₹98,050", note: "transfer leg" },
          { label: "Interest Account", value: "Cr ₹1,740", note: "month one, deducted" },
          { label: "Insurance Account", value: "Cr ₹150" },
          { label: "Other Charges Account", value: "Cr ₹60" },
        ],
        total: { label: "Debits = Credits", value: "₹1,50,000" },
        foot:
          "Check it: 50,000 + 98,050 + 1,740 + 150 + 60 = ₹1,50,000. The " +
          "deduction is not money vanishing — it is income recognised at " +
          "disbursement instead of cash going out.",
      },
      {
        type: "callout",
        tone: "info",
        text:
          "Because these are the same screens the rest of the product uses, their " +
          "**home** button brings you back to the pawn dashboard rather than " +
          "somewhere else, and the pawn sidebar stays with you throughout.",
      },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * 15. Wrap up
   * ══════════════════════════════════════════════════════════════════════════ */
  {
    id: "finish",
    title: "You're ready",
    tagline: "A short daily rhythm, the shortcuts worth memorising, and where to look things up",
    icon: <FinishIcon />,
    blocks: [
      {
        type: "para",
        text:
          "That is the whole module. Here is what it looks like once it is just " +
          "your job rather than something you are learning.",
      },
      { type: "h", text: "A day at the counter" },
      {
        type: "steps",
        items: [
          { label: "Open the Dashboard", text: "Check the loan book and yesterday's disbursement against your cash." },
          { label: "Work the Payment Screen", text: "New pledges: weigh, value, lend, print the ticket and the pouch label." },
          { label: "Work the Receipt Screen", text: "Interest collections and part payments. Use Price Breakup whenever a figure is questioned." },
          { label: "Settle what closes", text: "Verify the ornaments against the card, take the money, release the pouch." },
          { label: "Close the day on Reports", text: "Payment and Receipt registers for the day, then the Cash Book on Ledger Reports against your cash." },
        ],
      },
      { type: "h", text: "Worth memorising" },
      {
        type: "kbd",
        items: [
          { keys: "F1", text: "Customer lookup, on the Payment Screen" },
          { keys: "F2", text: "New customer" },
          { keys: "Ctrl + Enter", text: "Save" },
          { keys: "Enter", text: "In the ornament row, commits the weight and jumps to Add" },
          { keys: "Scan", text: "On the Receipt and Stock Check screens, a barcode scan is the same as typing the number and pressing Enter" },
        ],
      },
      { type: "h", text: "The three things people get wrong" },
      {
        type: "bullets",
        items: [
          "**Rates are monthly.** 1.16 % a month, not a year. Chapter 4.",
          "**Lend against net weight, not gross.** Stones and lac are not collateral. Chapter 6.",
          "**A short payment carries; it does not disappear.** Next month's demand includes it. Chapter 7.",
        ],
      },
      {
        type: "callout",
        tone: "tip",
        title: "Looking something up later",
        text:
          "Come back to any chapter from the list on the left at any time — the " +
          "worked examples are all in rupees against the same loan, so they are " +
          "quick to re-read. On the real screens, the ⓘ icons beside the interest " +
          "rules in Settings and the **Price Breakup** button on a receipt are the " +
          "two fastest answers to \"why this figure?\".",
      },
      {
        type: "para",
        text:
          "Lakshmi Devi's bangle came in on 10-Apr-2026 at ₹1,99,688, funded " +
          "₹1,50,000, paid ₹1,890 of interest in June, ₹50,000 off the principal, " +
          "and went home on 10-Aug-2026 for ₹1,01,260. Every screen in this module " +
          "exists to make that one story reliable — a few hundred times a month.",
      },
    ],
  },
];

export default CHAPTERS;
